export interface LanguageInfo {
  name: string;
  endonym?: string;
  region?: string;
  dir: "ltr" | "rtl";
}

export type isoLanguageCodes =
  | "mul"
  | "aa"
  | "ab"
  | "ae"
  | "af"
  | "ak"
  | "am"
  | "an"
  | "ar"
  | "as"
  | "av"
  | "ay"
  | "az"
  | "ba"
  | "be"
  | "bg"
  | "bh"
  | "bi"
  | "bm"
  | "bn"
  | "bo"
  | "br"
  | "bs"
  | "ca"
  | "ce"
  | "ch"
  | "co"
  | "cr"
  | "cs"
  | "cu"
  | "cv"
  | "cy"
  | "da"
  | "de"
  | "dv"
  | "dz"
  | "ee"
  | "el"
  | "en"
  | "en-GB"
  | "en-US"
  | "eo"
  | "es"
  | "es-419"
  | "es-ES"
  | "es-MX"
  | "et"
  | "eu"
  | "fa"
  | "ff"
  | "fi"
  | "fil"
  | "fj"
  | "fo"
  | "fr"
  | "fr-FR"
  | "fr-CA"
  | "fy"
  | "ga"
  | "gd"
  | "gl"
  | "gn"
  | "gu"
  | "gv"
  | "ha"
  | "he"
  | "hi"
  | "ho"
  | "hr"
  | "ht"
  | "hu"
  | "hy"
  | "hz"
  | "ia"
  | "id"
  | "ie"
  | "ig"
  | "ii"
  | "ik"
  | "io"
  | "is"
  | "it"
  | "iu"
  | "ja"
  | "jv"
  | "ka"
  | "kg"
  | "ki"
  | "kj"
  | "kk"
  | "kl"
  | "km"
  | "kn"
  | "ko"
  | "kr"
  | "ks"
  | "ku"
  | "kv"
  | "kw"
  | "ky"
  | "la"
  | "lb"
  | "lg"
  | "li"
  | "ln"
  | "lo"
  | "lt"
  | "lu"
  | "lv"
  | "mg"
  | "mh"
  | "mi"
  | "mk"
  | "ml"
  | "mn"
  | "mr"
  | "ms"
  | "mt"
  | "my"
  | "na"
  | "nb"
  | "nd"
  | "ne"
  | "ng"
  | "nl"
  | "nn"
  | "no"
  | "nr"
  | "nv"
  | "ny"
  | "oc"
  | "oj"
  | "om"
  | "or"
  | "os"
  | "pa"
  | "pi"
  | "pl"
  | "ps"
  | "pt"
  | "pt-BR"
  | "qu"
  | "rm"
  | "rn"
  | "ro"
  | "ru"
  | "rw"
  | "sa"
  | "sc"
  | "sd"
  | "se"
  | "sg"
  | "si"
  | "sk"
  | "sl"
  | "sm"
  | "sn"
  | "so"
  | "sq"
  | "sr"
  | "ss"
  | "st"
  | "su"
  | "sv"
  | "sw"
  | "ta"
  | "te"
  | "tg"
  | "th"
  | "ti"
  | "tk"
  | "tl"
  | "tn"
  | "to"
  | "tr"
  | "ts"
  | "tt"
  | "tw"
  | "ty"
  | "ug"
  | "uk"
  | "ur"
  | "uz"
  | "ve"
  | "vi"
  | "vo"
  | "wa"
  | "wo"
  | "xh"
  | "yi"
  | "yo"
  | "za"
  | "zh"
  | "zh-Hans"
  | "zh-Hant"
  | "zh-Hant-HK"
  | "zh-Hant-TW"
  | "zu";

/**
 * ------------------------------------------
 * BCP47 Language Tags
 * ------------------------------------------
 */
const Languages: {
  [languageCode in isoLanguageCodes]: LanguageInfo;
} = {
  mul: {
    name: "Multiple Languages",
    dir: "ltr"
  },
  aa: {
    name: "Afar",
    dir: "ltr"
  },
  ab: {
    name: "Abkhazian",
    dir: "ltr"
  },
  ae: {
    name: "Avestan",
    dir: "ltr"
  },
  af: {
    name: "Afrikaans",
    dir: "ltr"
  },
  ak: {
    name: "Akan",
    dir: "ltr"
  },
  am: {
    name: "Amharic",
    endonym: "አማርኛ",
    dir: "ltr"
  },
  an: {
    name: "Aragonese",
    dir: "ltr"
  },
  ar: {
    name: "Arabic",
    endonym: "عربي",
    dir: "rtl"
  },
  as: {
    name: "Assamese",
    dir: "ltr"
  },
  av: {
    name: "Avaric",
    dir: "ltr"
  },
  ay: {
    name: "Aymara",
    dir: "ltr"
  },
  az: {
    name: "Azerbaijani",
    dir: "ltr"
  },
  ba: {
    name: "Bashkir",
    dir: "ltr"
  },
  be: {
    name: "Belarusian",
    endonym: "беларуская",
    dir: "ltr"
  },
  bg: {
    name: "Bulgarian",
    endonym: "Български",
    dir: "ltr"
  },
  bh: {
    name: "Bihari Languages",
    dir: "ltr"
  },
  bi: {
    name: "Bislama",
    dir: "ltr"
  },
  bm: {
    name: "Bambara",
    dir: "ltr"
  },
  bn: {
    name: "Bengali",
    endonym: "বাংলা",
    dir: "ltr"
  },
  bo: {
    name: "Tibetan",
    dir: "ltr"
  },
  br: {
    name: "Breton",
    dir: "ltr"
  },
  bs: {
    name: "Bosnian",
    dir: "ltr"
  },
  ca: {
    name: "Catalan",
    dir: "ltr"
  },
  ce: {
    name: "Chechen",
    dir: "ltr"
  },
  ch: {
    name: "Chamorro",
    dir: "ltr"
  },
  co: {
    name: "Corsican",
    dir: "ltr"
  },
  cr: {
    name: "Cree",
    dir: "ltr"
  },
  cs: {
    name: "Czech",
    endonym: "Čeština",
    dir: "ltr"
  },
  cu: {
    name: "Church Slavonic",
    dir: "ltr"
  },
  cv: {
    name: "Chuvash",
    dir: "ltr"
  },
  cy: {
    name: "Welsh",
    dir: "ltr"
  },
  da: {
    name: "Danish",
    endonym: "Dansk",
    dir: "ltr"
  },
  de: {
    name: "German",
    endonym: "Deutsch",
    dir: "ltr"
  },
  dv: {
    name: "Divehi",
    dir: "rtl"
  },
  dz: {
    name: "Dzongkha",
    dir: "ltr"
  },
  ee: {
    name: "Ewe",
    dir: "ltr"
  },
  el: {
    name: "Modern Greek",
    endonym: "Ελληνικά",
    dir: "ltr"
  },
  en: {
    name: "English",
    dir: "ltr"
  },
  "en-GB": {
    name: "English",
    region: "British",
    dir: "ltr"
  },
  "en-US": {
    name: "English",
    region: "American",
    dir: "ltr"
  },
  eo: {
    name: "Esperanto",
    dir: "ltr"
  },
  es: {
    name: "Spanish",
    endonym: "Español",
    dir: "ltr"
  },
  "es-ES": {
    name: "Spanish (Spain)",
    endonym: "Español (España)",
    dir: "ltr"
  },
  "es-419": {
    name: "Spanish (Latin America)",
    endonym: "Español (Latinoamérica)",
    dir: "ltr"
  },
  "es-MX": {
    name: "Spanish (Mexico)",
    endonym: "Español (México)",
    dir: "ltr"
  },
  et: {
    name: "Estonian",
    endonym: "Eesti keel",
    dir: "ltr"
  },
  eu: {
    name: "Basque",
    dir: "ltr"
  },
  fa: {
    name: "Persian",
    dir: "rtl"
  },
  ff: {
    name: "Fulah",
    dir: "ltr"
  },
  fi: {
    name: "Finnish",
    endonym: "Suomi",
    dir: "ltr"
  },
  fil: {
    name: "Filipino",
    dir: "ltr"
  },
  fj: {
    name: "Fijian",
    dir: "ltr"
  },
  fo: {
    name: "Faroese",
    dir: "ltr"
  },
  fr: {
    name: "French",
    endonym: "Français",
    dir: "ltr"
  },
  "fr-FR": {
    name: "French",
    endonym: "Français (la France)",
    dir: "ltr"
  },
  "fr-CA": {
    name: "French (Canadian)",
    endonym: "Français (Canadien)",
    dir: "ltr"
  },
  fy: {
    name: "Western Frisian",
    dir: "ltr"
  },
  ga: {
    name: "Irish",
    dir: "ltr"
  },
  gd: {
    name: "Scottish Gaelic",
    dir: "ltr"
  },
  gl: {
    name: "Galician",
    dir: "ltr"
  },
  gn: {
    name: "Guarani",
    dir: "ltr"
  },
  gu: {
    name: "Gujarati",
    dir: "ltr"
  },
  gv: {
    name: "Manx",
    dir: "ltr"
  },
  ha: {
    name: "Hausa",
    dir: "rtl"
  },
  he: {
    name: "Hebrew",
    endonym: "עברית",
    dir: "rtl"
  },
  hi: {
    name: "Hindi",
    endonym: "हिन्दी",
    dir: "ltr"
  },
  ho: {
    name: "Hiri Motu",
    dir: "ltr"
  },
  hr: {
    name: "Croatian",
    endonym: "Hrvatski",
    dir: "ltr"
  },
  ht: {
    name: "Haitian Creole",
    dir: "ltr"
  },
  hu: {
    name: "Hungarian",
    endonym: "Magyar",
    dir: "ltr"
  },
  hy: {
    name: "Armenian",
    dir: "ltr"
  },
  hz: {
    name: "Herero",
    dir: "ltr"
  },
  ia: {
    name: "Interlingua",
    dir: "ltr"
  },
  id: {
    name: "Indonesian",
    endonym: "Bahasa Indonesia",
    dir: "ltr"
  },
  ie: {
    name: "Interlingue",
    dir: "ltr"
  },
  ig: {
    name: "Igbo",
    dir: "ltr"
  },
  ii: {
    name: "Sichuan Yi",
    dir: "ltr"
  },
  ik: {
    name: "Inupiaq",
    dir: "ltr"
  },
  io: {
    name: "Ido",
    dir: "ltr"
  },
  is: {
    name: "Icelandic",
    dir: "ltr"
  },
  it: {
    name: "Italian",
    endonym: "Italiano",
    dir: "ltr"
  },
  iu: {
    name: "Inuktitut",
    dir: "ltr"
  },
  ja: {
    name: "Japanese",
    endonym: "日本語",
    dir: "ltr"
  },
  jv: {
    name: "Javanese",
    dir: "ltr"
  },
  ka: {
    name: "Georgian",
    dir: "ltr"
  },
  kg: {
    name: "Kongo",
    dir: "ltr"
  },
  ki: {
    name: "Kikuyu",
    dir: "ltr"
  },
  kj: {
    name: "Kuanyama",
    dir: "ltr"
  },
  kk: {
    name: "Kazakh",
    dir: "ltr"
  },
  kl: {
    name: "Greenlandic",
    dir: "ltr"
  },
  km: {
    name: "Central Khmer",
    dir: "ltr"
  },
  kn: {
    name: "Kannada",
    dir: "ltr"
  },
  ko: {
    name: "Korean",
    endonym: "한국어",
    dir: "ltr"
  },
  kr: {
    name: "Kanuri",
    dir: "ltr"
  },
  ks: {
    name: "Kashmiri",
    dir: "rtl"
  },
  ku: {
    name: "Kurdish",
    dir: "rtl"
  },
  kv: {
    name: "Komi",
    dir: "ltr"
  },
  kw: {
    name: "Cornish",
    endonym: "Kernewek",
    dir: "ltr"
  },
  ky: {
    name: "Kirghiz",
    dir: "ltr"
  },
  la: {
    name: "Latin",
    dir: "ltr"
  },
  lb: {
    name: "Luxembourgish",
    endonym: "Lëtzebuergesch",
    dir: "ltr"
  },
  lg: {
    name: "Ganda",
    dir: "ltr"
  },
  li: {
    name: "Limburgish",
    dir: "ltr"
  },
  ln: {
    name: "Lingala",
    dir: "ltr"
  },
  lo: {
    name: "Lao",
    dir: "ltr"
  },
  lt: {
    name: "Lithuanian",
    endonym: "Lietuviškai",
    dir: "ltr"
  },
  lu: {
    name: "Luba-Katanga",
    dir: "ltr"
  },
  lv: {
    name: "Latvian",
    endonym: "Latviski",
    dir: "ltr"
  },
  mg: {
    name: "Malagasy",
    dir: "ltr"
  },
  mh: {
    name: "Marshallese",
    dir: "ltr"
  },
  mi: {
    name: "Māori",
    dir: "ltr"
  },
  mk: {
    name: "Macedonian",
    dir: "ltr"
  },
  ml: {
    name: "Malayalam",
    dir: "ltr"
  },
  mn: {
    name: "Mongolian",
    dir: "ltr"
  },
  mr: {
    name: "Marathi",
    dir: "ltr"
  },
  ms: {
    name: "Malay",
    endonym: "Bahasa Melayu",
    dir: "ltr"
  },
  mt: {
    name: "Maltese",
    dir: "ltr"
  },
  my: {
    name: "Burmese",
    dir: "ltr"
  },
  na: {
    name: "Nauruan",
    dir: "ltr"
  },
  nb: {
    name: "Norwegian Bokmål",
    dir: "ltr"
  },
  nd: {
    name: "Northern Ndebele",
    dir: "ltr"
  },
  ne: {
    name: "Nepali",
    dir: "ltr"
  },
  ng: {
    name: "Ndonga",
    dir: "ltr"
  },
  nl: {
    name: "Dutch",
    endonym: "Nederlands",
    dir: "ltr"
  },
  nn: {
    name: "Norwegian Nynorsk",
    dir: "ltr"
  },
  no: {
    name: "Norwegian",
    dir: "ltr"
  },
  nr: {
    name: "Southern Ndebele",
    dir: "ltr"
  },
  nv: {
    name: "Navajo",
    dir: "ltr"
  },
  ny: {
    name: "Chichewa",
    dir: "ltr"
  },
  oc: {
    name: "Occitan",
    dir: "ltr"
  },
  oj: {
    name: "Ojibwa",
    dir: "ltr"
  },
  om: {
    name: "Oromo",
    dir: "ltr"
  },
  or: {
    name: "Oriya",
    dir: "ltr"
  },
  os: {
    name: "Ossetian",
    dir: "ltr"
  },
  pa: {
    name: "Punjabi",
    dir: "ltr"
  },
  pi: {
    name: "Pali",
    dir: "ltr"
  },
  pl: {
    name: "Polish",
    endonym: "Polski",
    dir: "ltr"
  },
  ps: {
    name: "Pashto",
    dir: "rtl"
  },
  pt: {
    name: "Portuguese",
    endonym: "Português",
    dir: "ltr"
  },
  "pt-BR": {
    name: "Portuguese",
    region: "Brazilian",
    endonym: "Português (Brasileiro)",
    dir: "ltr"
  },
  qu: {
    name: "Quechua",
    dir: "ltr"
  },
  rm: {
    name: "Romansh",
    dir: "ltr"
  },
  rn: {
    name: "Rundi",
    dir: "ltr"
  },
  ro: {
    name: "Romanian",
    endonym: "Română",
    dir: "ltr"
  },
  ru: {
    name: "Russian",
    endonym: "Русский",
    dir: "ltr"
  },
  rw: {
    name: "Kinyarwanda",
    dir: "ltr"
  },
  sa: {
    name: "Sanskrit",
    dir: "ltr"
  },
  sc: {
    name: "Sardinian",
    dir: "ltr"
  },
  sd: {
    name: "Sindhi",
    dir: "rtl"
  },
  se: {
    name: "Northern Sami",
    dir: "ltr"
  },
  sg: {
    name: "Sango",
    dir: "ltr"
  },
  si: {
    name: "Sinhalese",
    dir: "ltr"
  },
  sk: {
    name: "Slovak",
    endonym: "Slovak",
    dir: "ltr"
  },
  sl: {
    name: "Slovenian",
    dir: "ltr"
  },
  sm: {
    name: "Samoan",
    dir: "ltr"
  },
  sn: {
    name: "Shona",
    dir: "ltr"
  },
  so: {
    name: "Somali",
    dir: "ltr"
  },
  sq: {
    name: "Albanian",
    dir: "ltr"
  },
  sr: {
    name: "Serbian",
    endonym: "Српски, Srpski",
    dir: "ltr"
  },
  ss: {
    name: "Swati",
    dir: "ltr"
  },
  st: {
    name: "Southern Sotho",
    dir: "ltr"
  },
  su: {
    name: "Sundanese",
    dir: "ltr"
  },
  sv: {
    name: "Swedish",
    endonym: "Svenska",
    dir: "ltr"
  },
  sw: {
    name: "Swahili",
    endonym: "Kiswahili",
    dir: "ltr"
  },
  ta: {
    name: "Tamil",
    endonym: "தமிழ் மொழி",
    dir: "ltr"
  },
  te: {
    name: "Telugu",
    dir: "ltr"
  },
  tg: {
    name: "Tajik",
    dir: "ltr"
  },
  th: {
    name: "Thai",
    endonym: "ไทย",
    dir: "ltr"
  },
  ti: {
    name: "Tigrinya",
    dir: "ltr"
  },
  tk: {
    name: "Turkmen",
    dir: "ltr"
  },
  tl: {
    name: "Tagalog",
    dir: "ltr"
  },
  tn: {
    name: "Tswana",
    dir: "ltr"
  },
  to: {
    name: "Tonga (Tonga Islands)",
    dir: "ltr"
  },
  tr: {
    name: "Turkish",
    endonym: "Türk",
    dir: "ltr"
  },
  ts: {
    name: "Tsonga",
    dir: "ltr"
  },
  tt: {
    name: "Tatar",
    dir: "ltr"
  },
  tw: {
    name: "Twi",
    dir: "ltr"
  },
  ty: {
    name: "Tahitian",
    dir: "ltr"
  },
  ug: {
    name: "Uighur",
    dir: "ltr"
  },
  uk: {
    name: "Ukrainian",
    endonym: "Українська",
    dir: "ltr"
  },
  ur: {
    name: "Urdu",
    endonym: "اردو",
    dir: "rtl"
  },
  uz: {
    name: "Uzbek",
    endonym: "Ўзбекча",
    dir: "ltr"
  },
  ve: {
    name: "Venda",
    dir: "ltr"
  },
  vi: {
    name: "Vietnamese",
    endonym: "Tiếng Việt",
    dir: "ltr"
  },
  vo: {
    name: "Volapük",
    dir: "ltr"
  },
  wa: {
    name: "Walloon",
    dir: "ltr"
  },
  wo: {
    name: "Wolof",
    dir: "ltr"
  },
  xh: {
    name: "Xhosa",
    dir: "ltr"
  },
  yi: {
    name: "Yiddish",
    dir: "rtl"
  },
  yo: {
    name: "Yoruba",
    dir: "ltr"
  },
  za: {
    name: "Zhuang",
    dir: "ltr"
  },
  zh: {
    name: "Chinese",
    endonym: "中文",
    dir: "ltr"
  },
  "zh-Hans": {
    name: "Chinese (Simplified)",
    endonym: "汉语",
    dir: "ltr"
  },
  "zh-Hant": {
    name: "Chinese (Traditional)",
    endonym: "漢語",
    dir: "ltr"
  },
  "zh-Hant-HK": {
    name: "Chinese (Traditional, Hong Kong)",
    endonym: "中文（繁体，香港",
    dir: "ltr"
  },
  "zh-Hant-TW": {
    name: "Chinese (Traditional, Taiwan)",
    endonym: "中文（正体，台湾",
    dir: "ltr"
  },
  zu: {
    name: "Zulu",
    dir: "ltr"
  }
};

export default Languages;
